class OutOfStockFormButton extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.isSubmitting = false;
    console.log('initialized');
  }

  connectedCallback() {
    this.addEventListeners();
    this.initializeOutOfStockModal();
  }

  disconnectedCallback() {
    this.removeEventListeners();
    const modal = document.querySelector('out-of-stock-form-modal');
    this.cleanupModalEventListeners(modal);
  }

  addEventListeners() {
    this.addEventListener('click', this.handleClick);
  }

  removeEventListeners() {
    this.removeEventListener('click', this.handleClick);
  }

  handleClick(event) {
    event.preventDefault();
    this.openOutOfStockForm();
  }

  initializeOutOfStockModal() {
    const modal = document.querySelector('out-of-stock-form-modal');
    if (!modal) return;
    
    // Check if modal is already globally initialized to prevent duplicate listeners
    if (!modal.dataset.listenersInitialized) {
      this.setupOutOfStockModalEventListeners();
      modal.dataset.listenersInitialized = 'true';
    }
  }

  setupOutOfStockModalEventListeners() {
    const modal = document.querySelector('out-of-stock-form-modal');
    if (!modal) return;

    // Remove existing listeners to prevent duplicates
    this.cleanupModalEventListeners(modal);

    const closeButton = modal.querySelector('.out-of-stock-form-modal__close-button');
    if (closeButton) {
      this.closeButtonHandler = () => this.hideOutOfStockModal(modal);
      closeButton.addEventListener('click', this.closeButtonHandler);
    }

    const overlay = modal.querySelector('.out-of-stock-form-modal__overlay');
    if (overlay) {
      this.overlayHandler = () => this.hideOutOfStockModal(modal);
      overlay.addEventListener('click', this.overlayHandler);
    }

    this.keydownHandler = (e) => {
      if (e.key === 'Escape' && modal.classList.contains('active')) {
        this.hideOutOfStockModal(modal);
      }
    };
    document.addEventListener('keydown', this.keydownHandler);

    const form = modal.querySelector('#out-of-stock-form');
    if (form) {
      this.formSubmitHandler = (e) => this.handleOutOfStockSubmit(e, modal);
      form.addEventListener('submit', this.formSubmitHandler);
    }
  }

  cleanupModalEventListeners(modal) {
    if (!modal) return;

    const closeButton = modal.querySelector('.out-of-stock-form-modal__close-button');
    if (closeButton && this.closeButtonHandler) {
      closeButton.removeEventListener('click', this.closeButtonHandler);
    }

    const overlay = modal.querySelector('.out-of-stock-form-modal__overlay');
    if (overlay && this.overlayHandler) {
      overlay.removeEventListener('click', this.overlayHandler);
    }

    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler);
    }

    const form = modal.querySelector('#out-of-stock-form');
    if (form && this.formSubmitHandler) {
      form.removeEventListener('submit', this.formSubmitHandler);
    }
  }

  getVariantData() {
    // Check if this button is within sticky ATC first
    const stickyAtc = this.closest('sticky-add-to-cart');
    let variantPicker;
    
    if (stickyAtc) {
      // If in sticky ATC, look for the variant picker within sticky ATC
      variantPicker = stickyAtc.querySelector('variant-picker');
    } else {
      // Otherwise, find the closest variant-picker element
      variantPicker =
        this.closest('variant-picker') ||
        this.parentElement?.querySelector('variant-picker') ||
        document.querySelector('variant-picker');
    }

    if (!variantPicker) {
      console.warn('No variant picker found');
      return {};
    }

    const variantData = {};

    // Get selected options from form inputs
    const form = variantPicker.querySelector('.variant-picker__form');
    if (form) {
      const selectedOptions = {};
      const radioInputs = form.querySelectorAll('input[type="radio"]:checked');

      radioInputs.forEach((input) => {
        const fieldset = input.closest('fieldset[data-option-id]');
        if (fieldset) {
          const legend = fieldset.querySelector('legend');
          let optionName = '';

          if (legend) {
            const spans = legend.querySelectorAll('span');
            if (spans.length > 0) {
              optionName = spans[0].textContent.trim();
            }
          }

          selectedOptions[optionName] = input.value;
        }
      });

      variantData.selectedOptions = selectedOptions;
    }

    return variantData;
  }

  openOutOfStockForm() {
    const productTitle = this.getAttribute('data-product-title');
    const productName = this.getAttribute('data-product-name');
    const productColor = this.getAttribute('data-product-color');
    const productId = this.getAttribute('data-product-id');
    const productImage = this.getAttribute('data-product-image');
    const productPrice = this.getAttribute('data-product-price');

    // Get variant data from closest variant picker
    const variantData = this.getVariantData();

    const quickAddDialog = this.closest('quick-add-dialog');
    if (quickAddDialog && typeof quickAddDialog.closeDialog === 'function') {
      quickAddDialog.closeDialog();
    }

    const currentUrl = window.location.href;
    const modal = document.querySelector('out-of-stock-form-modal');

    if (!modal) {
      console.error('Out of stock modal not found');
      return;
    }

    // Combine product data with variant data
    const combinedData = {
      title: productTitle,
      name: productName,
      color: productColor,
      url: currentUrl,
      id: productId,
      image: productImage,
      price: productPrice,
      ...variantData,
    };

    this.updateOutOfStockContent(modal, combinedData);
    this.showOutOfStockModal(modal);
  }

  updateOutOfStockContent(modal, productData) {
    const productImage = modal.querySelector('.out-of-stock-form-modal__product-image img');
    if (productImage && productData.image) {
      productImage.src = productData.image;
      productImage.alt = productData.name || 'Product Image';
    }

    const productTitle = modal.querySelector('.product-title');
    if (productTitle) {
      productTitle.textContent = productData.name || '';
    }

    const productColor = modal.querySelector('.product-color');
    if (productColor) {
      productColor.textContent = productData.color || '';
    }

    const productPrice = modal.querySelector('.product-price');
    if (productPrice) {
      productPrice.textContent = productData.price || '';
    }

    // Update product option name and value if they exist in the modal
    const productOptionName = modal.querySelector('.product-option-name');
    const productOptionValue = modal.querySelector('.product-option-value');

    if (productData.selectedOptions && Object.keys(productData.selectedOptions).length > 0) {
      // Combine all option names and values
      const optionNames = Object.keys(productData.selectedOptions);
      const optionValues = Object.values(productData.selectedOptions);

      const combinedOptionName = optionNames.join('/'); // "Gender/Size"
      const combinedOptionValue = optionValues.join('/'); // "Male/7"

      if (productOptionName && productOptionValue) {
        productOptionName.textContent = combinedOptionName || '';
        productOptionValue.textContent = combinedOptionValue || '';
      }
    }

    const modalTitle = modal.querySelector('.out-of-stock-form-modal__title');
    if (modalTitle) {
      modalTitle.textContent = `NOTIFY ME WHEN AVAILABLE`;
    }

    const form = modal.querySelector('#out-of-stock-form');
    if (form) {
      // Update existing form fields
      const productTitleInput = form.querySelector('input[name="product_title"]');
      const productUrlInput = form.querySelector('input[name="product_url"]');
      const productIdInput = form.querySelector('input[name="product_id"]');
      const productOptionNameInput = form.querySelector('input[name="product_option_name"]');
      const productOptionValueInput = form.querySelector('input[name="product_option_value"]');
      const dateTimeInput = form.querySelector('input[name="date_time"]');

      if (productTitleInput) productTitleInput.value = productData.title || '';
      if (productUrlInput) productUrlInput.value = productData.url || '';
      if (productIdInput) productIdInput.value = productData.id || '';

      // Set option name and value from selected options
      if (productData.selectedOptions && Object.keys(productData.selectedOptions).length > 0) {
        const optionNames = Object.keys(productData.selectedOptions);
        const optionValues = Object.values(productData.selectedOptions);

        const combinedOptionName = optionNames.join('/'); // "Gender/Size"
        const combinedOptionValue = optionValues.join('/'); // "Male/7"

        if (productOptionNameInput) productOptionNameInput.value = combinedOptionName || '';
        if (productOptionValueInput) productOptionValueInput.value = combinedOptionValue || '';
      }

      if (dateTimeInput) {
        const now = new Date();
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = now.getFullYear();
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        const formattedDateTime = `${day}/${month}/${year} - ${hours}:${minutes}:${seconds}`;
        dateTimeInput.value = formattedDateTime;
      }

      // Add selected options as hidden field
      this.addSelectedOptionsField(form, productData.selectedOptions);

      const customerEmailInput = form.querySelector('#customer_email');
      if (customerEmailInput) customerEmailInput.value = '';
    }
  }

  addSelectedOptionsField(form, selectedOptions) {
    // Remove existing selected options field to avoid duplicates
    const existingField = form.querySelector('input[name="selected_options"]');
    if (existingField) {
      existingField.remove();
    }

    // Add selected options as JSON string
    if (selectedOptions && Object.keys(selectedOptions).length > 0) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'selected_options';
      input.value = JSON.stringify(selectedOptions);
      form.appendChild(input);
    }
  }

  showOutOfStockModal(modal) {
    modal.classList.add('active');
    const container = modal.querySelector('.out-of-stock-form-modal__container');
    if (container) {
      container.classList.remove('closing');
      container.classList.add('active');
    }
    document.body.style.overflow = 'hidden';

    setTimeout(() => {
      const emailInput = modal.querySelector('#customer_email');
      if (emailInput) emailInput.focus();
    }, 100);
  }

  hideOutOfStockModal(modal) {
    const container = modal.querySelector('.out-of-stock-form-modal__container');
    if (container) {
      container.classList.remove('active');
      container.classList.add('closing');
    }
    modal.classList.remove('active');
    document.body.style.overflow = '';
  }

  handleOutOfStockSubmit(event, modal) {
    event.preventDefault();

    if (this.isSubmitting) {
      console.warn('Form already submitting...');
      return;
    }

    const form = event.target;
    const GOOGLE_APPS_SCRIPT_URL =
      'https://script.google.com/macros/s/AKfycbxyqToaiC9uMN2mkRFKdZ9t-g1KePpf7jB_s6-uvPCAKKNaMNL-qeEKKtFZ9g8-hbSmtA/exec';

    const customerEmail = form.querySelector('#customer_email').value;

    if (!customerEmail) {
      this.showOutOfStockError(form, 'Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerEmail)) {
      this.showOutOfStockError(form, 'Please enter a valid email address');
      return;
    }

    this.isSubmitting = true;

    const submitButton = form.querySelector('.custom-button_inner');
    const submitButtonText = form.querySelector('.custom-button_text');

    if (!submitButton) {
      this.isSubmitting = false;
      return;
    }

    const originalInnerText = submitButton.textContent;
    const originalDataText = submitButton.getAttribute('data-text') || 'Notify Me';

    submitButton.dataset.originalInnerText = originalInnerText;
    submitButton.dataset.originalDataText = originalDataText;

    submitButton.setAttribute('data-text', 'Submitting...');
    if (submitButtonText) {
      submitButtonText.textContent = 'Submitting...';
    }
    submitButton.disabled = true;

    const formData = new FormData(form);
    const data = Object.fromEntries(formData);

    // Parse selected options if it exists
    let selectedOptions = {};
    try {
      selectedOptions = JSON.parse(data.selected_options || '{}');
    } catch (error) {
      console.error('Error parsing selected options:', error);
    }

    fetch(GOOGLE_APPS_SCRIPT_URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        formId: 'form2',
        productName: data.product_title,
        productURL: data.product_url,
        productID: data.product_id,
        productOptionName: data.product_option_name,
        productOptionValue: data.product_option_value,
        dateTime: data.date_time,
        email: data.customer_email,
        selectedOptions: selectedOptions,
      }),
    })
      .then((response) => {
        this.showOutOfStockSuccess(form, modal);
      })
      .catch((error) => {
        console.error('Google Sheets Error:', error);
        this.showOutOfStockError(form, 'Error occurred. Please try again.');
      })
      .finally(() => {
        this.isSubmitting = false;
      });
  }

  showOutOfStockSuccess(form, modal) {
    const submitButton = form.querySelector('.custom-button_inner');
    const submitButtonText = form.querySelector('.custom-button_text');

    if (submitButton) {
      submitButton.setAttribute('data-text', 'Submitted');
      if (submitButtonText) {
        submitButtonText.textContent = 'Submitted';
      }
    }

    setTimeout(() => {
      this.hideOutOfStockModal(modal);
      setTimeout(() => {
        if (submitButton) {
          const originalInnerText = submitButton.dataset.originalInnerText || 'Notify Me';
          const originalDataText = submitButton.dataset.originalDataText || 'Notify Me';

          submitButton.setAttribute('data-text', originalDataText);
          if (submitButtonText) {
            submitButtonText.textContent = originalDataText;
          }
          submitButton.disabled = false;
        }
      }, 300);
    }, 2000);
  }

  showOutOfStockError(form, message = 'Error occurred') {
    const submitButton = form.querySelector('.custom-button_inner');
    const submitButtonText = form.querySelector('.custom-button_text');

    if (submitButton) {
      const originalInnerText = submitButton.dataset.originalInnerText || 'Notify Me';
      const originalDataText = submitButton.dataset.originalDataText || 'Notify Me';

      submitButton.setAttribute('data-text', message);
      if (submitButtonText) {
        submitButtonText.textContent = message;
      }

      setTimeout(() => {
        submitButton.setAttribute('data-text', originalDataText);
        if (submitButtonText) {
          submitButtonText.textContent = originalDataText;
        }
        submitButton.disabled = false;
      }, 3000);
    }
  }

  // Getter and setter methods remain the same
  get productTitle() {
    return this.getAttribute('data-product-title');
  }

  get productName() {
    return this.getAttribute('data-product-name');
  }

  get productColor() {
    return this.getAttribute('data-product-color');
  }

  get productId() {
    return this.getAttribute('data-product-id');
  }

  get productImage() {
    return this.getAttribute('data-product-image');
  }

  get productPrice() {
    return this.getAttribute('data-product-price');
  }

  set productTitle(value) {
    this.setAttribute('data-product-title', value);
  }

  set productName(value) {
    this.setAttribute('data-product-name', value);
  }

  set productColor(value) {
    this.setAttribute('data-product-color', value);
  }

  set productId(value) {
    this.setAttribute('data-product-id', value);
  }

  set productImage(value) {
    this.setAttribute('data-product-image', value);
  }

  set productPrice(value) {
    this.setAttribute('data-product-price', value);
  }

  updateProductData(productData) {
    if (productData.title) this.productTitle = productData.title;
    if (productData.name) this.productName = productData.name;
    if (productData.color) this.productColor = productData.color;
    if (productData.id) this.productId = productData.id;
    if (productData.image) this.productImage = productData.image;
    if (productData.price) this.productPrice = productData.price;
  }
}

customElements.define('out-of-stock-form-button', OutOfStockFormButton);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const modal = document.querySelector('out-of-stock-form-modal');
    if (modal && !modal.dataset.initialized) {
      modal.dataset.initialized = 'true';
    }
  });
} else {
  const modal = document.querySelector('out-of-stock-form-modal');
  if (modal && !modal.dataset.initialized) {
    modal.dataset.initialized = 'true';
  }
}
